import Logger from '../logging/logger';
import { NewContentLoadedEvent } from '../events/new-content-loaded';
import { getUser } from './user';

const selector = '.property-telephone';

function toggleCountryCodes( container: Document | Element ): void {
    const numbers = container.querySelectorAll<HTMLElement>( selector );

    if ( numbers.length === 0 ) {
        return;
    }

    numbers.forEach( ( number: HTMLElement ) => {
        if ( !number.dataset.altHref || number.classList.contains( 'property-telephone--updated' ) ) {
            return;
        }

        const href = number.getAttribute( 'href' );

        if ( href && href.length > 0 ) {
            number.setAttribute( 'href', `tel:${number.dataset.altHref}` );
            number.classList.add( 'no-country-code' );
        } else {
            number.innerHTML = number.dataset.altHref;

            if ( number.parentElement === null ) {
                throw new Error( 'parent element is null' );
            }

            number.parentElement.classList.add( 'no-country-code' );
        }

        number.classList.add( 'property-telephone--updated' );
    } );
}

export async function initCountryCodes(): Promise<void> {
    Logger.info( 'initialising telephone country codes' );

    const observer: IntersectionObserver = new IntersectionObserver( async ( entries ) => {
        if ( entries.some( ( entry ) => entry.isIntersecting ) ) {
            const user = getUser();

            if ( user.visitorInfo().country === 'ES' ) {
                toggleCountryCodes( document );
                observer.disconnect();
            } else {
                observer.disconnect();
                window.removeEventListener( 'twc:newcontentloaded', onContentLoaded );
            }
        }
    } );

    const observe = function( elements: NodeListOf<HTMLElement> ): void {
        elements.forEach( ( element: HTMLElement ) => {
            if ( !/--(observed|updated)/.test( element.className ) ) {
                Logger.info( 'observing telephone element', {
                    element: element,
                } );

                ( observer as IntersectionObserver ).observe( element );
                element.classList.add( 'property-telephone--observed' );
            }
        } );
    };

    const onContentLoaded = function( event: CustomEvent<NewContentLoadedEvent> ) {
        let container: HTMLElement | Document = document;

        if ( event.detail && event.detail.container ) {
            container = event.detail.container;
        }

        const elements = container.querySelectorAll<HTMLElement>( selector );

        if ( !elements ) {
            return;
        }

        observe( elements );
    };

    window.addEventListener( 'twc:newcontentloaded', onContentLoaded );

    observe( document.querySelectorAll<HTMLElement>( selector ) );
}

export function initClickToRevealTelephoneNumbers( container: Document | HTMLElement  = document ): void {
    function init( container: Document | HTMLElement ): void {
        const btns = container.querySelectorAll( 'button.clicktoreveal' );

        if ( !btns ) {
            return;
        }

        btns.forEach( ( btn: HTMLButtonElement ) => {
            if ( btn.dataset.hrefInitialised ) {
                return;
            }

            btn.dataset.hrefInitialised = 'true';

            btn.addEventListener( 'click', ( event: Event ) => {
                const hrefs = ( btn.dataset.href || '' ).split( ',' );
                const txts = ( btn.dataset.revealContent || '' ).split( ',' );

                const container = document.createElement( 'span' );
                container.className = 'clicktoreveal__revealedcontent';

                hrefs.forEach( ( href: string, index: number ) => {
                    const replacementElement = document.createElement( 'a' );
                    replacementElement.href = href;
                    replacementElement.innerHTML = txts[ index ];
                    container.append( replacementElement );

                    if ( index !== ( hrefs.length - 1 ) ) {
                        container.append( document.createTextNode( ' / ' ) );
                    }
                } );

                ( btn.parentElement as HTMLElement ).insertBefore( container, btn );

                btn.style.display = 'none';
            } );
        } );
    }

    window.addEventListener( 'twc:newcontentloaded', ( event: CustomEvent<NewContentLoadedEvent> ) => {
        let container: Document | HTMLElement = document;

        if ( event.detail.container ) {
            container = event.detail.container;
        }

        init( container );
    } );

    init( container );
}
